<template>
  <section id="projects" class="section projects">
    <div class="container">
      <div class="section-head projects__head">
        <p v-reveal class="eyebrow"><span class="eyebrow__index">03</span>Projects</p>
        <h2 v-reveal="80" class="section-title">Selected work</h2>
      </div>

      <div v-reveal="120" class="projects__inner">
        <div
          class="projects__list"
          role="tablist"
          aria-label="Projects"
          aria-orientation="vertical"
        >
          <div v-for="(project, i) in projects" :key="project.title" class="projects__item">
            <button
              :id="`project-tab-${i}`"
              ref="tabs"
              class="projects__tab"
              :class="{ 'is-active': i === active }"
              role="tab"
              type="button"
              :aria-selected="i === active ? 'true' : 'false'"
              :aria-controls="`project-panel-${i}`"
              :tabindex="i === active ? 0 : -1"
              @click="select(i)"
              @keydown="onKeydown($event, i)"
            >
              <span class="projects__tab-num">{{ String(i + 1).padStart(2, '0') }}</span>
              <span class="projects__tab-title">{{ project.title }}</span>
              <span class="projects__tab-chain">{{ project.chain }}</span>
              <svg
                class="projects__tab-arrow"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.6" />
              </svg>
            </button>
            <div
              v-if="isCompact && i === active"
              :id="`project-panel-${i}`"
              class="projects__panel projects__panel--inline"
              role="tabpanel"
              :aria-labelledby="`project-tab-${i}`"
            >
              <ProjectDetails :project="project" />
            </div>
          </div>
        </div>

        <div v-if="!isCompact" class="projects__aside">
          <transition name="fade" mode="out-in">
            <div
              :id="`project-panel-${active}`"
              :key="active"
              class="projects__panel"
              role="tabpanel"
              :aria-labelledby="`project-tab-${active}`"
            >
              <ProjectDetails :project="projects[active]" />
            </div>
          </transition>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
const ProjectDetails = {
  name: 'ProjectDetails',
  functional: true,
  props: {
    project: {
      type: Object,
      required: true
    }
  },
  render(h, { props: { project } }) {
    return h('article', { class: 'project' }, [
      h('div', { class: 'project__visual' }, [
        h('img', {
          class: ['project__img', project.wide && 'project__img--wide'],
          attrs: { src: project.img, alt: '', loading: 'lazy' }
        })
      ]),
      h('div', { class: 'project__body' }, [
        h('h3', { class: 'project__title' }, project.subtitle),
        h('div', { class: 'project__text', domProps: { innerHTML: project.text } }),
        h(
          'ul',
          { class: 'tags project__tags' },
          project.tags.map(tag => h('li', { key: tag }, tag))
        )
      ])
    ])
  }
}

export default {
  name: 'Projects',
  components: {
    ProjectDetails
  },
  data() {
    return {
      active: 0,
      projects: [
        {
          title: 'NFT ART',
          subtitle: 'NFT ART',
          chain: 'Ethereum',
          tags: ['ERC-721', 'Marketplace', 'Wallet connect'],
          text: '<p>The ERC-721 smart contract is developed with the&nbsp;mechanics of&nbsp;the&nbsp;marketplace at&nbsp;the&nbsp;blockchain level. The&nbsp;functions of&nbsp;selling, viewing all active lots, and buying are integrated within the&nbsp;contract.</p><p>The service allows users to&nbsp;create digital art (NFT) with the&nbsp;possibility of&nbsp;obtaining a&nbsp;physical version. Royalties, NFTs limit per user, vesting time are configurable parameters of&nbsp;the&nbsp;sales mechanism on&nbsp;the&nbsp;marketplace.</p><p>A landing page is created with a&nbsp;protocol for&nbsp;connecting crypto wallets.</p>',
          img: require('@/assets/img/eth.png')
        },
        {
          title: 'NFT Early Adopters',
          subtitle: 'NFT Early Adopters',
          chain: 'BSC',
          tags: ['ERC-721', 'Access passes', 'Proxy'],
          text: '<p>The NFT Early Adopters project is the&nbsp;ERC-721 smart contract on&nbsp;the&nbsp;Binance Smart Chain network.</p><p>Authentication tokens (passes), based on&nbsp;NFT, provide users with access to&nbsp;a&nbsp;wide range of&nbsp;services related to&nbsp;a&nbsp;Play-To-Earn game Axis Infinity. Passes are issued for&nbsp;maintaining activity in&nbsp;the&nbsp;social networks of&nbsp;the&nbsp;project. Proxy technology is implemented for&nbsp;future updates.</p>',
          img: require('@/assets/img/eth.png')
        },
        {
          title: 'NFT Wall Street Gang',
          subtitle: 'Wall Street Gang',
          chain: 'Ethereum',
          tags: ['OpenSea', 'IPFS', 'Gasless listing'],
          text: '<p>The Wall Street Gang collection at&nbsp;the&nbsp;OpenSea marketplace is created on&nbsp;the&nbsp;Ethereum network.</p><p>Advanced gasless listing technology is implemented for&nbsp;saving Ether. The&nbsp;pseudo random generation mechanism creates a&nbsp;collection of&nbsp;images that is securely stored on&nbsp;a&nbsp;distributed IPFS system. The&nbsp;private pre-sale function is built into&nbsp;the&nbsp;smart contract.</p><p><a href="http://gangws.com" target="_blank" rel="noopener">A landing page</a> is created with a&nbsp;protocol for&nbsp;connecting crypto wallets.</p>',
          img: require('@/assets/img/eth.png')
        },
        {
          title: 'What-To-Farm Token',
          subtitle: 'What-To-Farm Token',
          chain: 'BSC',
          tags: ['ERC-20', 'Staking', 'Reflect tokenomics'],
          text: '<p>The What-To-Farm ERC-20 token is created and a&nbsp;staking smart contract is developed.</p><p>The What-To-Farm token is implemented on&nbsp;the&nbsp;Binance Smart Chain (BSC) network. It is based on&nbsp;reflect tokenomics and follows high-security requirements. The&nbsp;information about the&nbsp;users&apos; deposits are stored on&nbsp;the&nbsp;blockchain. The&nbsp;interest rates are calculated using a&nbsp;unique mechanism. The&nbsp;protocol provides a&nbsp;choice of&nbsp;the&nbsp;aggregated staking pool tariff. Proxy technology is implemented for&nbsp;future updates.</p>',
          img: require('@/assets/img/globe.png')
        },
        {
          title: 'Cross-chain Router',
          subtitle: 'Cross-chain Router',
          chain: 'EVM',
          tags: ['deBridge', 'USDC', 'Routing'],
          text: '<p>A decentralized service of&nbsp;the&nbsp;What-To-Farm project is designed to&nbsp;route the&nbsp;exchange of&nbsp;tokens.</p><p>The contract implements a&nbsp;mechanism for&nbsp;transferring tokens from one EVM network to&nbsp;another. Cross-chain operations go through DeBridge, a&nbsp;partner company. Token transfer between networks is carried out through the&nbsp;USDС stablecoin, and then into&nbsp;the&nbsp;cryptocurrency chosen by the&nbsp;user. The&nbsp;service performs cross-chain exchange along the&nbsp;optimal route with the&nbsp;lowest commission.</p>',
          img: require('@/assets/img/globe.png')
        },
        {
          title: 'Cryptorobotics Token',
          subtitle: 'Cryptorobotics Token & Vesting',
          chain: 'BSC',
          tags: ['BEP-20', 'Vesting', 'Utility token'],
          text: '<p>The DeRobo BEP-20 token on&nbsp;the&nbsp;Binance Smart Chain is designed for&nbsp;Cryptorobotics, a&nbsp;company engaged in&nbsp;trading bots and algotrading.</p><p>DeRobo is a&nbsp;utility token of&nbsp;the&nbsp;company. Tokens are used to&nbsp;pay for&nbsp;services on&nbsp;the&nbsp;exchange. It is used to&nbsp;participate in&nbsp;raffles, fee payments, and rent trading bots. DeRobo&apos;s liquidity is provided via a&nbsp;one-way bridge from the&nbsp;Ethereum network. The&nbsp;token vesting contract is developed for&nbsp;investors, influencers and the&nbsp;community.</p>',
          img: require('@/assets/img/coin.png'),
          wide: true
        },
        {
          title: 'Cryptorobotics Bridge',
          subtitle: 'Cryptorobotics Bridge',
          chain: 'ETH → BSC',
          tags: ['Bridge', 'Burn & mint', 'Whitelist'],
          text: '<p>The one-way liquidity bridge project is developed for&nbsp;Cryptorobotics, a&nbsp;company engaged in&nbsp;trading bots and algotrading. A mechanism of&nbsp;transferring the&nbsp;DeRobo tokens from the&nbsp;Ethereum network to&nbsp;the&nbsp;Binance Smart Chain is implemented.</p><p>DeRobo tokens are burned in&nbsp;the&nbsp;original network. The&nbsp;contract automatically transmits information to&nbsp;the&nbsp;BSC, followed by the&nbsp;distribution of&nbsp;tokens to&nbsp;users.</p><p>The landing page for&nbsp;the&nbsp;cryptocurrency bridge is created. A whitelist mechanism is implemented.</p>',
          img: require('@/assets/img/coin.png'),
          wide: true
        },
        {
          title: 'AlgoEURS',
          subtitle: 'AlgoEURS',
          chain: 'Ethereum',
          tags: ['DeFi', 'Stablecoin', 'Insurance'],
          text: '<p>Development of&nbsp;the&nbsp;AlgoEURS architecture.</p><p>AlgoEURS is a&nbsp;decentralized finance (DeFi) protocol on&nbsp;Ethereum blockchain that allows users to&nbsp;borrow AEUR, a&nbsp;stablecoin pegged to&nbsp;the&nbsp;euro, using a&nbsp;wide range of&nbsp;on-chain assets as collateral including plain Ether, other stablecoins, and non-fungible token (NFT).</p><p>The&nbsp;protocol allows borrowers to&nbsp;insure against the&nbsp;liquidation of&nbsp;the&nbsp;collateral assets and provides a&nbsp;platform for&nbsp;investors to&nbsp;make money as insurers. To create the&nbsp;possibility of&nbsp;insuring the&nbsp;collateral and making money on&nbsp;it, the&nbsp;protocol issues AEPUT token on&nbsp;the&nbsp;Ethereum blockchain.</p>',
          img: require('@/assets/img/axie.png')
        },
        {
          title: 'Ether Orcs',
          subtitle: 'Ether Orcs',
          chain: 'ETH · Polygon',
          tags: ['Audit', 'Analytics', 'Telegram bot'],
          text: '<p>A project is developed to&nbsp;collect analytics for&nbsp;the&nbsp;on-chain game Ether Orcs.</p><p>An audit of&nbsp;the&nbsp;game smart contracts is conducted both for&nbsp;Ethereum and Polygon networks. A&nbsp;couple of&nbsp;dozen reports are created with suggestions for&nbsp;optimizing the&nbsp;game strategy, taking into&nbsp;account the&nbsp;priorities of&nbsp;the&nbsp;customer.</p><p>The mechanism is designed for&nbsp;data collection and visualization of&nbsp;game statistics on&nbsp;a&nbsp;web page. The&nbsp;online monitoring system of&nbsp;all important game mechanics is implemented. A&nbsp;telegram bot is created to&nbsp;receive analytics and instant crash notifications.</p>',
          img: require('@/assets/img/nft.png')
        }
      ]
    }
  },
  computed: {
    isCompact() {
      return this.$root.isSmallTablet
    }
  },
  methods: {
    select(i) {
      this.active = i
      if (this.isCompact) {
        this.$nextTick(() => {
          const tab = this.$refs.tabs[i]
          if (tab && tab.getBoundingClientRect().top < 64) {
            tab.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
      }
    },
    onKeydown(event, i) {
      const keys = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }
      let next = null
      if (keys[event.key]) {
        next = (i + keys[event.key] + this.projects.length) % this.projects.length
      } else if (event.key === 'Home') {
        next = 0
      } else if (event.key === 'End') {
        next = this.projects.length - 1
      }
      if (next === null) {
        return
      }
      event.preventDefault()
      this.select(next)
      this.$refs.tabs[next].focus()
    }
  }
}
</script>
